import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { typeIcons, InlineEdit, InlineEditCheckbox, locationLabel } from "../components/Utils";
import axios from "../axiosConfig";
import NotesTable from "../components/NotesTable";
import TasksTable from "../components/TasksTable";
import InteractionsTable from "../components/InteractionsTable";
import Modal from "../components/Modal";
import PageTitle from "../components/PageTitle";

// Offices on offer for a contact (GET /contacts/<id>/'s office_options: its
// account's locations and its parent/child accounts'), keeping the current
// office selectable even if it's no longer among them.
const officeOptionsFor = (data) => {
  const options = data?.office_options || [];
  if (data?.office && !options.some((l) => l.id === data.office.id)) {
    return [...options, data.office];
  }
  return options;
};

const Contact = () => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [user, setUser] = useState(null);

  // References to table components for cross-refreshing
  const tasksTableRef = useRef(null);
  const interactionsTableRef = useRef(null);

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");

  // user is now managed by useState above

  // Fetch contact and preload locations
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        setUser(storedUser);
      }
    } catch {
      console.warn("Invalid user in localStorage");
    }
    const fetchContact = async () => {
      try {
        const response = await axios.get(`/contacts/${id}/`, {
          withCredentials: true,
        });
        const data = response.data.contact;
        console.log("Contact data:", data);

        const normalized = {
          ...data,
          tutor: Boolean(data?.tutor),
          attendee_only: Boolean(data?.attendee_only),
          temp_public_course: Boolean(data?.temp_public_course),
          status: Boolean(data?.status),
        };

        setContact(normalized);

        // Update page title
        const isStaging =
          (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
        document.title = `${data.first_name} ${data.last_name} - FT Portal${isStaging ? " (Staging)" : ""}`;

        setLocations(officeOptionsFor(data));
      } catch (error) {
        console.error("Error fetching contact:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [id]);

  // Reset page title on unmount
  useEffect(() => {
    const isStaging =
      (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
    return () => {
      document.title = isStaging ? "FT Portal (Staging)" : "FT Portal";
    };
  }, []);

  // Delete contact
  const handleDelete = async () => {
    try {
      await axios.delete(`/contacts/${contact.id}/`, { withCredentials: true });
      window.location.href = "/contacts";
    } catch (err) {
      console.error("Failed to delete contact:", err);
    }
  };

  // Callback functions for cross-table refreshing
  const handleTaskCreated = () => {
    // When an interaction becomes a task, refresh tasks table
    if (tasksTableRef.current && tasksTableRef.current.fetchTasks) {
      tasksTableRef.current.fetchTasks();
    }
  };

  const handleInteractionCreated = () => {
    // When a task becomes an interaction, refresh interactions table
    if (
      interactionsTableRef.current &&
      interactionsTableRef.current.fetchInteractions
    ) {
      interactionsTableRef.current.fetchInteractions();
    }
  };

  const handleInlineSave = async (field, newValue) => {
    try {
      let payload;
      let contactUpdate;

      const boolFields = ["tutor", "attendee_only", "temp_public_course", "status"];

      if (field === "account") {
        payload = { account_id: newValue?.id || null };
        contactUpdate = { account: newValue, office: null };
      } else if (field === "office") {
        payload = { office: newValue };
        const loc = locations.find((l) => String(l.id) === String(newValue));
        contactUpdate = { office: loc || null };
      } else if (boolFields.includes(field)) {
        // Sent by InlineEditCheckbox as true/false.
        const boolVal = newValue === true;
        payload = { [field]: boolVal };
        contactUpdate = { [field]: boolVal };
      } else {
        payload = { [field]: newValue };
        contactUpdate = { [field]: newValue };
      }

      const response = await axios.patch(`/contacts/${id}/`, payload, {
        withCredentials: true,
      });
      if (response.data.success) {
        setContact((prev) => ({ ...prev, ...contactUpdate }));
        if (field === "account") {
          // The offices on offer depend on the account.
          const resp = await axios.get(`/contacts/${id}/`, {
            withCredentials: true,
          });
          setLocations(officeOptionsFor(resp.data.contact));
        }
      }
    } catch (err) {
      console.error("Failed to update contact field:", err);
    }
  };

  if (loading)
    return (
      <div>
        <p>Loading contact...</p>
      </div>
    );
  if (!contact)
    return (
      <div>
        <p>Contact not found.</p>
      </div>
    );

  const defaultParticipants = [
    ...(user
      ? [
          {
            id: user.user_contact_id,
            type: "contact",
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            is_sender: false,
          },
        ]
      : []),
    ...(contact.id !== user?.user_contact_id
      ? [
          {
            id: contact.id,
            type: "contact",
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email,
            is_sender: false,
          },
        ]
      : []),
    ...(contact.account
      ? [
          {
            id: contact.account.id,
            type: "account",
            name_short: contact.account.name_short,
            is_sender: false,
          },
        ]
      : []),
  ];

  return (
    <div className="page-layout">
      <PageTitle>
        {typeIcons["contact"]} {contact.first_name} {contact.last_name}
      </PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-btn"
          >
            Delete Contact
          </button>
        </div>
      </div>

      <div className="page-content">
        <table className="details-table">
          <tbody>
            <tr>
              <td>
                <div className="details-label">Full Name</div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <InlineEdit
                    field="title"
                    value={contact.title || ""}
                    onSave={handleInlineSave}
                    placeholder="Title"
                  />
                  <InlineEdit
                    field="first_name"
                    value={contact.first_name || ""}
                    onSave={handleInlineSave}
                  />
                  <InlineEdit
                    field="last_name"
                    value={contact.last_name || ""}
                    onSave={handleInlineSave}
                  />
                </div>
              </td>
              <td>
                <div className="details-label">Email</div>
                <InlineEdit
                  field="email"
                  type="email"
                  value={contact.email || ""}
                  onSave={handleInlineSave}
                  quickEmailParticipant={{
                    id: contact.id,
                    type: "contact",
                    first_name: contact.first_name,
                    last_name: contact.last_name,
                  }}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Company</div>
                <InlineEdit
                  field="account"
                  type="account"
                  value={contact.account}
                  displayValue={
                    contact.account?.id ? (
                      <Link to={`/account?id=${contact.account.id}`}>
                        {contact.account.name_short ||
                          contact.account.name_long}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Office Switchboard</div>
                <InlineEdit
                  field="switchboard_number"
                  type="phone"
                  value={contact.office?.switchboard_number || ""}
                  phoneCountry={contact.office?.country}
                  disabled
                  disabled_reason="Change office switchboard on account page"
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Job Title</div>
                <InlineEdit
                  field="job_title"
                  value={contact.job_title || ""}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Office Phone</div>
                <InlineEdit
                  field="office_phone"
                  type="phone"
                  value={contact.office_phone || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Office</div>
                <InlineEdit
                  field="office"
                  type="dropdown"
                  value={String(contact.office?.id || "")}
                  displayValue={locationLabel(contact.office)}
                  options={locations.map((loc) => ({
                    value: String(loc.id),
                    // Other accounts' locations are labelled with the account.
                    label:
                      loc.account_name_short &&
                      loc.account_id !== contact.account?.id
                        ? `${loc.account_name_short}: ${locationLabel(loc)}`
                        : locationLabel(loc),
                  }))}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Mobile Phone</div>
                <InlineEdit
                  field="mobile_phone"
                  type="phone"
                  value={contact.mobile_phone || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Tutor</div>
                <InlineEditCheckbox
                  field="tutor"
                  value={contact.tutor}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Attendee Only</div>
                <InlineEditCheckbox
                  field="attendee_only"
                  value={contact.attendee_only}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Public Course</div>
                <InlineEditCheckbox
                  field="temp_public_course"
                  value={contact.temp_public_course}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Active</div>
                <InlineEditCheckbox
                  field="status"
                  value={contact.status}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Notes</h3>
        <NotesTable
          contactId={contact.id}
          contactName={contact.first_name + " " + contact.last_name}
        />

        <hr />

        <h3>Tasks</h3>
        <TasksTable
          contactId={contact.id}
          defaultParticipants={defaultParticipants}
          onInteractionCreated={handleInteractionCreated}
          ref={tasksTableRef}
        />

        <h3>Interactions</h3>
        <InteractionsTable
          contactId={contact.id}
          defaultParticipants={defaultParticipants}
          onTaskCreated={handleTaskCreated}
          ref={interactionsTableRef}
        />

        {/* Delete Modal */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Confirm Delete Contact"
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>
              {contact.first_name} {contact.last_name}
            </strong>
            ? This action cannot be undone.
          </p>
          <div>
            <button onClick={handleDelete} style={{ color: "red" }}>
              Yes, Delete
            </button>
            <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Contact;
